import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import api from '../services/api'
import Card from '../components/Card'
import LoadingSpinner from '../components/LoadingSpinner'
import { getToken, saveUserData } from '../utils/userData'
import { ROUTES } from '../utils/constants'

const ParticipantDashboard = () => {
  const [loading, setLoading] = useState(true)
  const [userData, setUserData] = useState(null)
  const [enrolledPrograms, setEnrolledPrograms] = useState([])
  const [discoverPrograms, setDiscoverPrograms] = useState([])
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [events, setEvents] = useState([])
  const [eventsLoading, setEventsLoading] = useState(true)

  useEffect(() => {
    const token = getToken()
    setIsAuthenticated(!!token)
    fetchData(token)
    fetchEvents()
  }, [])

  const fetchData = async (token) => {
    setLoading(true)
    
    try {
      // Fetch all active programs
      const programsResponse = await api.get('/api/programs?active=true')
      const allProgramsList = programsResponse.data.success ? programsResponse.data.data : []

      // If authenticated, fetch user data with enrollments
      if (token) {
        try {
          const meResponse = await api.get('/api/auth/me')
          if (meResponse.data.success) {
            const data = meResponse.data.data
            setUserData(data)
            saveUserData(data)
            setEnrolledPrograms(data.enrolledPrograms || [])
            
            // Filter out enrolled programs for discover section
            const enrolledSlugs = (data.enrolledPrograms || []).map(p => p.programSlug)
            const unenrolled = allProgramsList.filter(
              p => !enrolledSlugs.includes(p.slug)
            )
            setDiscoverPrograms(unenrolled)
          }
        } catch (err) {
          console.error('Error fetching user data:', err)
          // If 401, user is not authenticated
          if (err.response?.status === 401) {
            setIsAuthenticated(false)
            setDiscoverPrograms(allProgramsList)
          }
        }
      } else {
        // Not authenticated - all programs go to discover
        setDiscoverPrograms(allProgramsList)
      }
    } catch (err) {
      console.error('Error fetching programs:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchEvents = async () => {
    try {
      setEventsLoading(true)
      const response = await api.get('/api/events')
      // Filter to only upcoming events (eventDate >= today)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const upcomingEvents = (response.data.data || []).filter(event => {
        const eventDate = new Date(event.eventDate)
        return eventDate >= today
      })
      setEvents(upcomingEvents)
    } catch (err) {
      console.error('Error fetching events:', err)
    } finally {
      setEventsLoading(false)
    }
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatTime = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getEventImage = (event) => {
    if (event.image) {
      if (event.image.startsWith('http')) return event.image
      return `${import.meta.env.VITE_API_URL || ''}${event.image}`
    }
    return '/yogastudio.jpg'
  }

  const getLocationText = (event) => {
    if (event.space) {
      return event.space.name + (event.space.city ? `, ${event.space.city}` : '')
    }
    return event.location || (event.format === 'online' ? 'Online' : 'Location TBA')
  }

  const getFacilitatorNames = (event) => {
    const names = []
    if (event.facilitators && event.facilitators.length > 0) {
      names.push(...event.facilitators.map(f => f.name))
    }
    if (event.externalFacilitators && event.externalFacilitators.length > 0) {
      names.push(...event.externalFacilitators)
    }
    return names
  }

  const getFormatBadgeColor = (format) => {
    switch (format?.toLowerCase()) {
      case 'online':
        return 'bg-blue-100 text-blue-800'
      case 'in-person':
        return 'bg-green-100 text-green-800'
      case 'hybrid':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getProgramTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'cohort':
        return 'from-purple-500 to-indigo-600'
      case 'workshop':
        return 'from-orange-500 to-red-500'
      case 'event':
        return 'from-teal-500 to-cyan-600'
      default:
        return 'from-primary-500 to-primary-700'
    }
  }

  const getProgramIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'cohort':
        return '👥'
      case 'workshop':
        return '🛠️'
      case 'event':
        return '🎉'
      default:
        return '✨'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100">
      <div className="container mx-auto px-4 py-12">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {isAuthenticated && userData?.name 
                ? `Welcome back, ${userData.name.split(' ')[0]}!` 
                : 'Welcome to Wellness Loop'}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {isAuthenticated 
                ? 'Track your wellness journey and discover new programs'
                : 'Explore our wellness programs and events to start your journey'}
            </p>
          </div>
        </motion.div>

        {/* My Programs Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-16"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center">
              <span className="mr-3">📚</span>
              My Programs
            </h2>
          </div>

          {isAuthenticated ? (
            enrolledPrograms.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {enrolledPrograms.map((enrollment, index) => (
                  <motion.div
                    key={enrollment.programId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full hover:shadow-xl transition-all bg-white overflow-hidden group">
                      <div className={`h-2 bg-gradient-to-r ${getProgramTypeColor(enrollment.role)}`} />
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <span className="text-4xl">{getProgramIcon('cohort')}</span>
                          <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                            Enrolled
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                          {enrollment.programName}
                        </h3>
                        <p className="text-sm text-gray-500 mb-4">
                          Enrolled on {new Date(enrollment.enrolledAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-600 capitalize">
                            Role: {enrollment.role}
                          </span>
                          <Link
                            to={`/${enrollment.programSlug}/dashboard`}
                            className="text-primary-600 hover:text-primary-700 font-semibold text-sm flex items-center"
                          >
                            Go to Dashboard
                            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </Link>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <Card className="bg-white p-8 text-center">
                <div className="text-6xl mb-4">🌱</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No Programs Yet</h3>
                <p className="text-gray-600 mb-6">
                  You have not enrolled in any programs yet. Explore our offerings below!
                </p>
              </Card>
            )
          ) : (
            <Card className="bg-gradient-to-r from-primary-600 to-primary-700 p-8 text-white">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex-1 text-center md:text-left">
                  <div className="text-5xl mb-4">🔐</div>
                  <h3 className="text-2xl font-bold mb-2">Sign In to View Your Programs</h3>
                  <p className="text-white/90">
                    Access your enrolled programs, track your progress, and continue your wellness journey.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to={ROUTES.COHORT_SIGNUP('energizers-june-cohort')}
                    className="px-6 py-3 bg-white text-primary-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors text-center"
                  >
                    Join June Cohort
                  </Link>
                  <Link
                    to={ROUTES.COHORT_SIGNUP('energizers-march-cohort')}
                    className="px-6 py-3 bg-white/10 text-white font-semibold rounded-xl border border-white/30 hover:bg-white/20 transition-colors text-center"
                  >
                    Join March Cohort
                  </Link>
                </div>
              </div>
            </Card>
          )}
        </motion.section>

        {/* Live Events Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center">
              <span className="mr-3">🎪</span>
              Discover Upcoming Events
            </h2>
            <Link
              to="/events"
              className="text-primary-600 hover:text-primary-700 font-semibold flex items-center"
            >
              View All Events
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>

          {eventsLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="md" />
            </div>
          ) : events.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.slice(0, 6).map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
              >
                <Card 
                  className="h-full cursor-pointer hover:shadow-xl transition-all bg-white overflow-hidden group"
                  onClick={() => setSelectedEvent(event)}
                >
                  {/* Event Image */}
                  <div className="h-40 overflow-hidden bg-gray-200">
                    <img 
                      src={getEventImage(event)} 
                      alt={event.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.src = '/yogastudio.jpg'
                      }}
                    />
                  </div>
                  
                  <div className="p-5">
                    {/* Format Badge */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getFormatBadgeColor(event.format)}`}>
                        {event.format}
                      </span>
                      {event.fee?.toLowerCase() === 'free' && (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                          FREE
                        </span>
                      )}
                    </div>

                    {/* Event Name */}
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {event.name}
                    </h3>

                    {/* Date and Time */}
                    <div className="flex items-center gap-2 text-gray-600 mb-2 text-sm">
                      <span>📅</span>
                      <span className="font-medium">{formatDate(event.eventDate)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600 mb-3 text-sm">
                      <span>⏰</span>
                      <span>{formatTime(event.eventDate)}{event.endDate && ` - ${formatTime(event.endDate)}`}</span>
                    </div>

                    {/* Location */}
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <span>📍</span>
                      <span className="truncate">{getLocationText(event)}</span>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
            </div>
          ) : (
            <Card className="bg-white p-8 text-center">
              <div className="text-6xl mb-4">📅</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Upcoming Events</h3>
              <p className="text-gray-600 mb-4">
                Check back soon for new events or browse our past events.
              </p>
              <Link
                to="/events"
                className="inline-block px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
              >
                Browse All Events
              </Link>
            </Card>
          )}
        </motion.section>

        {/* Discover Programs Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center">
              <span className="mr-3">🔍</span>
              Discover Programs
            </h2>
          </div>

          {discoverPrograms.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {discoverPrograms.map((program, index) => (
                <motion.div
                  key={program.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-xl transition-all bg-white overflow-hidden group">
                    <div className={`h-2 bg-gradient-to-r ${getProgramTypeColor(program.type)}`} />
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <span className="text-4xl">{getProgramIcon(program.type)}</span>
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full capitalize ${
                          program.type === 'cohort' ? 'bg-purple-100 text-purple-700' :
                          program.type === 'workshop' ? 'bg-orange-100 text-orange-700' :
                          'bg-teal-100 text-teal-700'
                        }`}>
                          {program.type || 'Program'}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                        {program.name}
                      </h3>
                      {program.description && (
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                          {program.description}
                        </p>
                      )}
                      {(program.startDate || program.endDate) && (
                        <div className="flex items-center gap-2 text-gray-500 text-sm mb-4">
                          <span>📅</span>
                          <span>
                            {program.startDate && new Date(program.startDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}
                            {program.startDate && program.endDate && ' - '}
                            {program.endDate && new Date(program.endDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      )}
                      <div className="pt-4 border-t border-gray-100">
                        <span className="text-sm text-gray-500">
                          Learn more about this program
                        </span>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <Card className="bg-white p-8 text-center">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">All Caught Up!</h3>
              <p className="text-gray-600">
                You have enrolled in all available programs. Stay tuned for new offerings!
              </p>
            </Card>
          )}
        </motion.section>

        {/* Event Detail Modal */}
        <AnimatePresence>
          {selectedEvent && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedEvent(null)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="sticky top-0 bg-gradient-to-r from-primary-600 to-primary-700 text-white p-6 rounded-t-2xl">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="mb-3 flex flex-wrap gap-2">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold bg-white/20`}>
                          {selectedEvent.format}
                        </span>
                        {selectedEvent.fee?.toLowerCase() === 'free' && (
                          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-yellow-400 text-yellow-900">
                            FREE
                          </span>
                        )}
                      </div>
                      <h2 className="text-2xl font-bold mb-2">{selectedEvent.name}</h2>
                      {selectedEvent.fee && selectedEvent.fee.toLowerCase() !== 'free' && (
                        <p className="text-xl font-bold">{selectedEvent.fee}</p>
                      )}
                    </div>
                    <button
                      onClick={() => setSelectedEvent(null)}
                      className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="p-6">
                  {/* Event Image */}
                  <div className="w-full h-48 overflow-hidden rounded-xl bg-gray-200 mb-6">
                    <img 
                      src={getEventImage(selectedEvent)} 
                      alt={selectedEvent.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = '/yogastudio.jpg'
                      }}
                    />
                  </div>

                  {/* Date, Time, Location */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">📅</span>
                      <div>
                        <p className="font-semibold text-gray-900">{formatDate(selectedEvent.eventDate)}</p>
                        <p className="text-gray-600">
                          {formatTime(selectedEvent.eventDate)}
                          {selectedEvent.endDate && ` - ${formatTime(selectedEvent.endDate)}`}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="text-2xl">📍</span>
                      <div>
                        <p className="text-gray-900">{getLocationText(selectedEvent)}</p>
                        {(selectedEvent.venueLink || selectedEvent.space?.googleMapsUrl) && (
                          <a 
                            href={selectedEvent.venueLink || selectedEvent.space?.googleMapsUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:underline text-sm"
                          >
                            View on Map →
                          </a>
                        )}
                      </div>
                    </div>

                    {getFacilitatorNames(selectedEvent).length > 0 && (
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">👤</span>
                        <div>
                          <p className="font-semibold text-gray-900">Facilitators</p>
                          <p className="text-gray-600">{getFacilitatorNames(selectedEvent).join(', ')}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">About This Event</h3>
                    <p className="text-gray-700 leading-relaxed">
                      {selectedEvent.shortDescription}
                    </p>
                  </div>

                  {/* Detailed Description */}
                  {selectedEvent.detailedDescription && (
                    <div className="mb-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-3">Details</h3>
                      <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                        {selectedEvent.detailedDescription}
                      </div>
                    </div>
                  )}

                  {/* CTA */}
                  <div className="bg-primary-50 rounded-xl p-6">
                    <Link
                      to="/events"
                      className="block w-full px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors text-center"
                    >
                      View All Event Details
                    </Link>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default ParticipantDashboard

